import { GoogleMap, DirectionsRenderer, MarkerF } from '@react-google-maps/api';
import React, { useEffect, useState, useContext, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import { getSvgUrl, lockIcon, vehicleIcon } from '../Constants';
import { ThemeContext } from '../App';

const useStyles = makeStyles({
  textStyle: {
    fontSize: '12px',
    color: '#000000',
    fontWeight: '600'
  },
  textStyleLight: {
    fontSize: '12px',
    color: '#848484'
  },
  infoWindow: {
    background: '#FFF',
    borderRadius: '10px',
    padding: '10px',
    minWidth: '200px',
    maxWidth: '270px',
    boxShadow: '0px 10px 19px 1px #EAEAFF'
  },
  infoHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  driverInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '5px'
  },
  iconText: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    marginBottom: '5px'
  },
  mapLabel: {
    color: 'rgb(0 0 0 / 70%) !important',
    marginTop: '-40px',
    fontSize: '10px !important',
    background: 'white !important',
    boxShadow: '0 4px 8px rgb(0 0 0 / 20%)',
    padding: '4px',
    whiteSpace: 'pre',
    width: 'auto',
    fontFamily: '"Montserrat", sans-serif',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    borderRadius: '4px'
  },
  mapLabelFirstLine: {
    color: 'rgb(0 0 0 / 85%) !important',
    fontSize: '10px !important',
    fontFamily: '"Montserrat", sans-serif'
  }
});

export default function MapPathView({ tripData, markerPoint, locationPoints }) {
  const isLoaded = useContext(ThemeContext);

  const classes = useStyles();
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [direction, setDirection] = useState(null);
  const [dragged, setDragged] = useState(false);
  const [map, setMap] = useState(null);
  const [devicePoints, setDevicePoints] = useState([]);
  const [center, setCenter] = useState({ lat: 28.499359150742354, lng: 77.06975633821554 });
  const [stations, setStations] = useState([]);

  const [networkOnline, setNetworkOnline] = useState(navigator.onLine);

  const options = {
    mapTypeControl: false,
    streetViewControl: false,
    zoomControl: false,
    scaleControl: false,
    keyboardShortcuts: false
  };

  const mapStyles = {
    height: '100%',
    width: '100%'
  };

  const getCords = (location) => {
    if (location) {
      const cords = location?.split(',');
      return {
        lat: parseFloat(cords[0]),
        lng: parseFloat(cords[1])
      };
    }
    return {
      lat: 0,
      lng: 0
    };
  };

  const getDeviceName = (deviceImei, data) => {
    const item = data?.find((item) => item?.lock_id === deviceImei);
    return item ? item?.name : null;
  };

  useEffect(() => {
    const handleOnline = () => setNetworkOnline(true);
    const handleOffline = () => setNetworkOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const transformedMarkerPoints =
      markerPoint?.map((point) => ({
        location_lat: point?.body?.location?.geo?.lat || 0,
        location_lng: point?.body?.location?.geo?.lng || 0,
        deviceImei: point?.body?.lock?.id || ''
      })) || [];

    setStations([...(locationPoints || []), ...transformedMarkerPoints]);
  }, [markerPoint, locationPoints]);

  useEffect(() => {
    if (!map || !devicePoints) return;
    const bounds = computeBounds(window.google.maps, devicePoints);
    if (bounds === null) return;
    if (!dragged) {
      map.fitBounds(bounds, 70);
      setCenter(bounds.getCenter());
    }
    setDevicePoints(devicePoints);
  }, [devicePoints, dragged, map]);

  useEffect(() => {
    if (stations?.length > 0 && networkOnline) {
      const maxPoints = 25;
      const parts = Array.from(
        { length: Math.ceil(stations.length / (maxPoints - 1)) },
        (_, index) => stations.slice(index * (maxPoints - 1), (index + 1) * (maxPoints - 1))
      );

      const fetchRoutes = async () => {
        const directionsPromises = parts.map((part) => {
          const waypoints = part.slice(1, part.length - 1).map((station) => ({
            location: new window.google.maps.LatLng(
              parseFloat(station.location_lat),
              parseFloat(station.location_lng)
            ),
            stopover: false
          }));

          const directionsService = new window.google.maps.DirectionsService();
          return directionsService.route({
            origin: new window.google.maps.LatLng(
              parseFloat(part[0].location_lat),
              parseFloat(part[0].location_lng)
            ),
            destination: new window.google.maps.LatLng(
              parseFloat(part[part.length - 1].location_lat),
              parseFloat(part[part.length - 1].location_lng)
            ),
            waypoints,
            travelMode: window.google.maps.TravelMode.WALKING
          });
        });

        const directionsResponses = await Promise.all(directionsPromises);
        setDirection(directionsResponses);
      };

      fetchRoutes();
    }
  }, [stations]);

  useEffect(() => {
    if (!window?.google || !window?.google.maps) {
      console.error('Google Maps API not loaded.');
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();

    const waypoints = tripData?.data?.route?.waypoints?.map((waypoint) => ({
      location: getCords(waypoint.location),
      stopover: true
    }));
    if (Object.keys(tripData)?.length !== 0) {
      directionsService?.route(
        {
          origin: getCords(tripData?.data?.route?.startLocation?.location),
          destination: getCords(tripData?.data?.route?.endLocation?.location),
          travelMode: window.google.maps.TravelMode.DRIVING,
          waypoints
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            setDirectionsResponse(result);
          } else {
            console.error(`Directions service failed due to ${status}`);
          }
        }
      );
    }
  }, [tripData]);

  const onMapLoad = useCallback((map) => setMap(map), []);

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={mapStyles}
      zoom={15}
      onLoad={onMapLoad}
      onDrag={() => setDragged(true)}
      center={center}
      options={options}
    >
      {/* Directions Renderer for the complete trip route */}
      {isLoaded && directionsResponse && (
        <DirectionsRenderer
          directions={directionsResponse}
          options={{
            polylineOptions: {
              strokeColor: 'gray',
              strokeWeight: 0,
              strokeOpacity: 0,
              icons: [
                {
                  icon: {
                    path: 'M 0,-1 0,1',
                    scale: 3,
                    strokeOpacity: 1,
                    strokeColor: 'gray',
                    strokeWeight: 5
                  },
                  offset: '0',
                  repeat: '15px'
                }
              ]
            }
          }}
          // options={{
          //   polylineOptions: {
          //     strokeColor: tripData?.data?.status === 'completed' ? 'Blue' : 'gray',
          //     strokeWeight: tripData?.data?.status === 'completed' ? 4 : 0,
          //     strokeOpacity: tripData?.data?.status === 'completed' ? 1 : 0,
          //     icons:
          //       tripData?.data?.status === 'completed'
          //         ? []
          //         : [
          //             {
          //               icon: {
          //                 path: 'M 0,-1 0,1',
          //                 scale: 3,
          //                 strokeOpacity: 1,
          //                 strokeColor: 'gray',
          //                 strokeWeight: 5
          //               },
          //               offset: '0',
          //               repeat: '15px'
          //             }
          //           ]
          //   }
          // }}
        />
      )}
      {direction?.map((response, index) => (
        <DirectionsRenderer
          key={index}
          directions={response}
          options={{
            polylineOptions: {
              strokeColor: '#00aaff',
              strokeWeight: 3,
              strokeOpacity: 1
            },
            suppressMarkers: true,
            preserveViewport: true
          }}
        />
      ))}
      <MarkerF
        position={
          stations?.length > 0
            ? {
                lat: parseFloat(stations[stations.length - 1]?.location_lat) || 0,
                lng: parseFloat(stations[stations.length - 1]?.location_lng) || 0
              }
            : { lat: 0, lng: 0 }
        }
        icon={vehicleIcon(tripData?.data?.vehicle?.type)}
        // icon={{
        //   url:
        //     tripData?.data?.status === 'completed' ? getSvgUrl('#505050', 24) : lockIcon('#027148')
        // }}
        label={{
          text: getDeviceName(stations[0]?.deviceImei, tripData?.devices),
          className: classes.mapLabel
        }}
      />
    </GoogleMap>
  ) : null;
}

const computeBounds = (map, devicePoints) =>
  !map || devicePoints?.length === 0
    ? null
    : devicePoints.reduce(
        (bounds, device) =>
          bounds.union(
            new map.LatLngBounds({
              lat: device?.location_lat,
              lng: device?.location_lng
            })
          ),
        new map.LatLngBounds({
          lat: devicePoints[0]?.location_lat,
          lng: devicePoints[0]?.location_lng
        })
      );
